import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { AdminPage, AdminState, Notice, PrimaryButton, isForbidden } from '@/admin/AdminShell'
import { StateView } from '@/card/record'
import { failureText } from '@/card/recordKit'
import {
  createRoutingRule,
  deleteRoutingRule,
  fetchRouting,
  updateRoutingRule,
  type AssignMode,
  type RoutingRule,
  type RoutingRuleInput,
  type RoutingSetup,
} from './supportTriageApi'

const KEY = ['admin-support-routing'] as const
const MODES: AssignMode[] = ['user', 'department_head', 'keep']

/**
 * Правила маршрутизации обращений по разбору ИИ (#132), право `system.settings` — как правила
 * распределения лидов (#103): кому достаются резкие претензии, решается для всей поддержки.
 *
 * Коды категорий, тональностей и срочностей экран берёт из ответа сервера, а не из своей
 * копии: правило с кодом, которого нет в справочнике, сервер не сохранит, а экран, держащий
 * свой список, однажды предложил бы именно такой. Подписи — из словаря значений очереди.
 */
export function SupportRoutingPage() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const setup = useQuery({ queryKey: KEY, queryFn: ({ signal }) => fetchRouting(signal), retry: false })
  const [draft, setDraft] = useState<Draft | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const mayUpdate = can(user, 'system.settings', 'update')

  const refresh = () => {
    setFailure(null)
    void queryClient.invalidateQueries({ queryKey: KEY })
  }
  const fail = (error: unknown) => setFailure(failureText(error, t('app.error')))
  const save = useMutation({
    mutationFn: (value: Draft) => (value.id ? updateRoutingRule(value.id, input(value)) : createRoutingRule(input(value))),
    onSuccess: () => {
      setDraft(null)
      refresh()
    },
    onError: fail,
  })
  const toggle = useMutation({
    mutationFn: (rule: RoutingRule) => updateRoutingRule(rule.id, { active: !rule.active }),
    onSuccess: refresh,
    onError: fail,
  })
  const remove = useMutation({ mutationFn: (id: string) => deleteRoutingRule(id), onSuccess: refresh, onError: fail })

  const rules = [...(setup.data?.rules ?? [])].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name))
  const nextPriority = rules.length === 0 ? 10 : Math.max(...rules.map((rule) => rule.priority)) + 10

  return (
    <AdminPage
      titleKey="nav.adminSupportRouting"
      leadKey="routing.intro"
      forbidden={isForbidden(setup.error)}
      readOnly={!mayUpdate}
      // Only once the rules are loaded: the new rule's priority goes after the last one.
      primary={mayUpdate && !draft && setup.data && (
        <PrimaryButton label={t('routing.add')} onClick={() => setDraft(emptyDraft(nextPriority))} />
      )}
    >
      <Notice text={failure} />
      <AdminState isLoading={setup.isLoading} error={setup.error} onRetry={() => void setup.refetch()}>
        {setup.data && (
          <div className="stack" style={{ gap: 12 }}>
            {draft && (
              <RuleForm
                draft={draft}
                setup={setup.data}
                busy={save.isPending}
                onChange={setDraft}
                onCancel={() => setDraft(null)}
                onSave={() => save.mutate(draft)}
              />
            )}
            {rules.length === 0 ? (
              <StateView boxed kind="empty" title={t('routing.empty')} />
            ) : (
              <ol className="stack" style={{ gap: 8, listStyle: 'none', padding: 0, margin: 0 }} aria-label={t('routing.title')}>
                {rules.map((rule) => (
                  <li key={rule.id} className="panel" style={{ padding: 10 }} data-rule={rule.id}>
                    <div className="row" style={{ gap: 8 }}>
                      <span className="strong">{rule.name}</span>
                      <span className={`badge ${rule.active ? 'b-success' : 'b-muted'}`}>
                        {rule.active ? t('routing.active') : t('routing.inactive')}
                      </span>
                      <span className="spacer" />
                      {mayUpdate && (
                        <>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggle.mutate(rule)}>
                            {rule.active ? t('routing.inactive') : t('routing.active')}
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(toDraft(rule))}>
                            {t('routing.edit')}
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => remove.mutate(rule.id)}>
                            {t('routing.delete')}
                          </button>
                        </>
                      )}
                    </div>
                    <p className="t-sm" style={{ margin: '4px 0 0' }}>
                      {t('routing.conditions')}: {conditionText(t, rule)}
                    </p>
                    <p className="t-sm" style={{ margin: '2px 0 0' }}>
                      {t('routing.actions')}: {actionText(t, rule)}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </AdminState>
    </AdminPage>
  )
}

type Translate = (key: string, params?: Record<string, string | number>) => string

function words(t: Translate, column: string, codes: string[] | undefined): string {
  const list = codes ?? []
  return list.length === 0 ? t('routing.any') : list.map((code) => t(`value.support.ticket.${column}.${code}`)).join(', ')
}

function conditionText(t: Translate, rule: RoutingRule): string {
  return [
    `${t('routing.categories')} — ${words(t, 'triage_category', rule.categories)}`,
    `${t('routing.tones')} — ${words(t, 'triage_tone', rule.tones)}`,
    `${t('routing.urgencies')} — ${words(t, 'triage_urgency', rule.urgencies)}`,
  ].join('; ')
}

function actionText(t: Translate, rule: RoutingRule): string {
  const parts: string[] = []
  if (rule.assignMode === 'user') parts.push(`${t('routing.mode.user')}: ${rule.assignUserName ?? rule.assignUserId ?? '—'}`)
  else parts.push(t(`routing.mode.${rule.assignMode}`))
  if (rule.markUrgent) parts.push(t('routing.markUrgent'))
  return parts.join('; ')
}

interface Draft {
  id?: string
  name: string
  priority: number
  active: boolean
  categories: string[]
  tones: string[]
  urgencies: string[]
  assignMode: AssignMode
  assignUserId: string
  markUrgent: boolean
}

function emptyDraft(priority: number): Draft {
  return { name: '', priority, active: true, categories: [], tones: [], urgencies: [], assignMode: 'user', assignUserId: '', markUrgent: false }
}

function toDraft(rule: RoutingRule): Draft {
  return {
    id: rule.id,
    name: rule.name,
    priority: rule.priority,
    active: rule.active,
    categories: rule.categories ?? [],
    tones: rule.tones ?? [],
    urgencies: rule.urgencies ?? [],
    assignMode: rule.assignMode,
    assignUserId: rule.assignUserId ?? '',
    markUrgent: rule.markUrgent,
  }
}

function input(draft: Draft): RoutingRuleInput {
  return {
    name: draft.name,
    priority: draft.priority,
    active: draft.active,
    categories: draft.categories,
    tones: draft.tones,
    urgencies: draft.urgencies,
    assignMode: draft.assignMode,
    assignUserId: draft.assignMode === 'user' && draft.assignUserId !== '' ? draft.assignUserId : null,
    markUrgent: draft.markUrgent,
  }
}

function RuleForm({ draft, setup, busy, onChange, onCancel, onSave }: {
  draft: Draft
  setup: RoutingSetup
  busy: boolean
  onChange: (draft: Draft) => void
  onCancel: () => void
  onSave: () => void
}) {
  const { t } = useApp()
  const toggleCode = (field: 'categories' | 'tones' | 'urgencies', code: string) => {
    const current = draft[field]
    onChange({ ...draft, [field]: current.includes(code) ? current.filter((c) => c !== code) : [...current, code] })
  }
  const group = (field: 'categories' | 'tones' | 'urgencies', column: string, codes: string[]) => (
    <fieldset className="stack" style={{ gap: 4 }}>
      <legend className="t-sm">{t(`routing.${field}`)}</legend>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
        {codes.map((code) => (
          <label key={code} className="row" style={{ gap: 4 }}>
            <input type="checkbox" checked={draft[field].includes(code)} onChange={() => toggleCode(field, code)} />
            {t(`value.support.ticket.${column}.${code}`)}
          </label>
        ))}
      </div>
    </fieldset>
  )

  return (
    <section className="panel" style={{ padding: 12 }} aria-label={t(draft.id ? 'routing.edit' : 'routing.add')}>
      <div className="stack" style={{ gap: 10 }}>
        <label className="stack" style={{ gap: 4 }}>
          <span className="t-sm">{t('routing.name')}</span>
          <input className="input" value={draft.name} aria-label={t('routing.name')}
                 onChange={(event) => onChange({ ...draft, name: event.target.value })} />
        </label>
        <label className="stack" style={{ gap: 4 }}>
          <span className="t-sm">{t('routing.priority')}</span>
          <input className="input" type="number" value={draft.priority} aria-label={t('routing.priority')}
                 onChange={(event) => onChange({ ...draft, priority: Number(event.target.value) })} />
        </label>
        {group('categories', 'triage_category', setup.categories)}
        {group('tones', 'triage_tone', setup.tones)}
        {group('urgencies', 'triage_urgency', setup.urgencies)}
        <label className="stack" style={{ gap: 4 }}>
          <span className="t-sm">{t('routing.assign')}</span>
          <select className="input" value={draft.assignMode} aria-label={t('routing.assign')}
                  onChange={(event) => onChange({ ...draft, assignMode: event.target.value as AssignMode })}>
            {MODES.map((mode) => <option key={mode} value={mode}>{t(`routing.mode.${mode}`)}</option>)}
          </select>
        </label>
        {draft.assignMode === 'user' && (
          <label className="stack" style={{ gap: 4 }}>
            <span className="t-sm">{t('routing.user')}</span>
            <select className="input" value={draft.assignUserId} aria-label={t('routing.user')}
                    onChange={(event) => onChange({ ...draft, assignUserId: event.target.value })}>
              <option value="">—</option>
              {setup.candidates.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
            </select>
          </label>
        )}
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={draft.markUrgent} onChange={() => onChange({ ...draft, markUrgent: !draft.markUrgent })} />
          {t('routing.markUrgent')}
        </label>
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn btn-primary" disabled={busy || draft.name.trim() === ''} onClick={onSave}>
            {t('routing.save')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>{t('routing.cancel')}</button>
        </div>
      </div>
    </section>
  )
}
